import { ShowDetails } from 'types/api/media';
import MoreInfoModal, {
  DESCRIPTION_LINE_HEIGHT,
  DESCRIPTION_WIDTH,
  FONT_SIZE,
  MoreInfoModalTemplateSpec,
} from 'components/common/moreInfoModal/MoreInfoModal';
import { ttsTagRemoval } from 'support/generalUtils';
import { translate } from 'support/translate';
import { isMovie } from 'support/contentUtils';
import TextLabel from '../TextLabel';

export interface ShowMoreInfoModalTemplateSpec
  extends MoreInfoModalTemplateSpec {
  mediaContent: ShowDetails | undefined;

  Container: MoreInfoModalTemplateSpec['Container'] & {
    Casts: typeof TextLabel;
    ProductionTeam: typeof TextLabel;
  };
}

const METADATA_MARGIN_TOP = 15;

const CAST_MARGIN_TOP = 20;
const CAST_MAX_LINES = 2;

const DIRECTORS_MARGIN_TOP = 20;
const DIRECTORS_MAX_LINES = 2;

export default class ShowMoreInfoModal extends MoreInfoModal<ShowMoreInfoModalTemplateSpec> {
  override _Container = (this as ShowMoreInfoModal).getByRef('Container')!;
  private _Casts = this._Container.getByRef('Casts')!;
  private _ProductionTeam = this._Container.getByRef('ProductionTeam')!;

  override get title() {
    const title = super.title;
    return [
      ...title,
      ttsTagRemoval(this._Casts.text?.text ?? ''),
      ttsTagRemoval(this._ProductionTeam.text?.text ?? ''),
    ];
  }

  protected get logoSrc() {
    return this._mediaContent?.images.image_logo_white ?? '';
  }

  override set mediaContent(mediaContent: ShowDetails) {
    super.mediaContent = mediaContent;
    this.setupCasts(mediaContent);
    this.setupProductionTeam(mediaContent);
  }

  static override _template() {
    const baseTemplate = super._template();
    return {
      ...baseTemplate,
      Container: {
        ...baseTemplate.Container,
        Casts: {
          type: TextLabel,
          flexItem: { marginTop: CAST_MARGIN_TOP },
          w: DESCRIPTION_WIDTH,
          text: {
            advancedRenderer: true,
            maxLines: CAST_MAX_LINES,
            lineHeight: DESCRIPTION_LINE_HEIGHT,
            fontSize: FONT_SIZE,
            wordBreak: true,
          },
        },
        ProductionTeam: {
          type: TextLabel,
          flexItem: { marginTop: DIRECTORS_MARGIN_TOP },
          w: DESCRIPTION_WIDTH,
          text: {
            advancedRenderer: true,
            maxLines: DIRECTORS_MAX_LINES,
            lineHeight: DESCRIPTION_LINE_HEIGHT,
            fontSize: FONT_SIZE,
            wordBreak: true,
          },
        },
      },
    };
  }

  override _active() {
    super._active();

    this._Metadata.patch({ flexItem: { marginTop: METADATA_MARGIN_TOP } });
  }

  override updateTitle() {
    this._Title.patch({ visible: false });
  }

  private setupCasts(show: ShowDetails) {
    const { showCast } = show;
    if (!showCast) return;

    this._Casts.patch({
      text: {
        text: translate('moreInfo.castBoldWithNames', showCast),
      },
    });
  }

  private setupProductionTeam(show: ShowDetails) {
    let text = '';
    if (isMovie(show)) {
      text = translate('moreInfo.directorsBoldWithNames', show.showDirectors);
    } else {
      text = translate(
        'moreInfo.executiveProducersBoldWithNames',
        show.showProducers,
      );
    }

    this._ProductionTeam.patch({ text: { text } });
  }
}
